import { World, ACT_FIRE } from "./world";
import {
  DT, RESCAN_INTERVAL_TICKS, PROJ_BOLT, PROJ_ARC, FRIENDLY_FIRE_MULT,
  OWNER_NEUTRAL, KIND_UNIT, KIND_BUILDING,
} from "./constants";
import { spawnWreckage } from "./entities";
import { dist } from "./math";
import type { Sim } from "./sim";

export const GRAVITY = 260; // world units / s^2 for ballistic arcs

function isEnemyTarget(w: World, e: number, c: number): boolean {
  if (!w.alive[c]) return false;
  const oc = w.owner[c];
  if (oc === OWNER_NEUTRAL || oc === w.owner[e]) return false;
  return w.kind[c] === KIND_UNIT || w.kind[c] === KIND_BUILDING;
}

// --- Amortized target acquisition (staggered rescans, GDD §9.6). ---
export function targetingStep(w: World, sim: Sim): void {
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.underConstruction(e)) continue;
    const def = w.def(e);
    const isBomb = def.role === "bomb";
    if (!def.weapon && !isBomb) continue;

    // Drop dead/invalid targets immediately.
    const cur = w.targetEnt[e];
    if (cur >= 0 && !isEnemyTarget(w, e, cur)) w.targetEnt[e] = -1;

    // Only fully rescan on this entity's staggered phase tick.
    if ((w.tick + w.rescanPhase[e]) % RESCAN_INTERVAL_TICKS !== 0) continue;

    const acqRange = isBomb
      ? def.sightRadius
      : Math.max(def.weapon!.range * 1.2, def.sightRadius * 0.7);
    const px = w.posX[e];
    const py = w.posY[e];
    const wr = isBomb ? 0 : def.weapon!.range;

    let best = -1;
    let bestScore = -Infinity;
    sim.hash.query(px, py, acqRange, (c) => {
      if (c === e || !isEnemyTarget(w, e, c)) return;
      const d = dist(px, py, w.posX[c], w.posY[c]);
      if (d > acqRange) return;
      const cdef = w.def(c);
      const inRange = d <= wr;
      // Threat+proximity: prefer in-range, then armed threats, then closeness.
      let score = -d;
      if (inRange) score += 4000;
      if (cdef.weapon) score += 300;
      if (cdef.role === "commander") score += 150;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    });
    // Keep a still-valid commanded target if rescan found nothing better nearby.
    if (best >= 0) w.targetEnt[e] = best;
  }
}

// --- Weapons firing + bomb detonation. ---
export function weaponsStep(w: World, sim: Sim): void {
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.underConstruction(e)) continue;
    const def = w.def(e);

    if (def.role === "bomb" && def.suicide) {
      const tg = w.targetEnt[e];
      if (w.isAlive(tg)) {
        const d = dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]);
        if (d <= def.radius + w.def(tg).radius + 8) {
          detonate(w, sim, e, def.suicide.damage, def.suicide.aoeRadius, w.posX[e], w.posY[e]);
          sim.fxEvents.push({ type: "explosion", x: w.posX[e], y: w.posY[e], radius: def.suicide.aoeRadius, faction: factionIdx(w, e) });
          w.health[e] = 0; // dies in deaths step (bomb leaves no wreckage; handled there)
          w.flags[e] |= 0; // no-op marker
          w.buildProgress[e] = 0; // signal: no reclaim value
        }
      }
      continue;
    }

    if (!def.weapon || w.reload[e] > 0) continue;
    const weap = def.weapon;
    const order = w.orders[e][0];
    let px = 0;
    let py = 0;
    let havePoint = false;
    const tg = w.targetEnt[e];
    if (w.isAlive(tg) && dist(w.posX[e], w.posY[e], w.posX[tg], w.posY[tg]) <= weap.range) {
      px = w.posX[tg];
      py = w.posY[tg];
      havePoint = true;
    } else if (order && order.kind === "attackGround" && dist(w.posX[e], w.posY[e], order.x, order.y) <= weap.range) {
      px = order.x;
      py = order.y;
      havePoint = true;
    }
    if (!havePoint) continue;

    const eco = w.eco[w.owner[e]];
    if (eco.energy < weap.energyPerShot) continue;
    eco.energy -= weap.energyPerShot;
    eco.energyExpense += weap.energyPerShot / DT;

    fire(w, sim, e, tg, px, py);
    w.reload[e] = weap.reloadTime;
    w.fireFlash[e] = 0.09;
    w.action[e] = ACT_FIRE;
  }
}

function fire(w: World, sim: Sim, e: number, tg: number, aimX: number, aimY: number): void {
  const def = w.def(e);
  const weap = def.weapon!;
  const p = sim.proj;
  const pi = p.spawn();
  if (pi < 0) return;

  const sx = w.posX[e] + Math.cos(w.heading[e]) * (def.radius + 2);
  const sy = w.posY[e] + Math.sin(w.heading[e]) * (def.radius + 2);

  // Lead the target for straight bolts.
  let tx = aimX;
  let ty = aimY;
  const flightDist = dist(sx, sy, aimX, aimY);
  if (w.isAlive(tg)) {
    const lead = flightDist / weap.projectileSpeed;
    const leadFactor = weap.projectileKind === "bolt" ? 1 : 0.5;
    tx = aimX + w.velX[tg] * lead * leadFactor;
    ty = aimY + w.velY[tg] * lead * leadFactor;
  }
  // Accuracy spread grows with range.
  const err = weap.accuracyError * (0.4 + 0.6 * (flightDist / weap.range));
  tx += sim.rng.jitter(err);
  ty += sim.rng.jitter(err);

  p.x[pi] = sx;
  p.y[pi] = sy;
  p.z[pi] = 0;
  p.px[pi] = sx;
  p.py[pi] = sy;
  p.pz[pi] = 0;
  p.damage[pi] = weap.damage;
  p.aoe[pi] = weap.aoeRadius;
  p.owner[pi] = w.owner[e];
  p.target[pi] = tg;
  p.faction[pi] = factionIdx(w, e);

  const dx = tx - sx;
  const dy = ty - sy;
  const dh = Math.hypot(dx, dy) || 1;

  if (weap.projectileKind === "arc") {
    p.ptype[pi] = PROJ_ARC;
    p.gravity[pi] = GRAVITY;
    const v = weap.projectileSpeed;
    const ratio = (GRAVITY * dh) / (v * v);
    let theta: number;
    if (ratio >= 1) {
      theta = Math.PI / 4; // out of range: fire for max distance
    } else {
      const low = 0.5 * Math.asin(ratio);
      theta = weap.arc >= 2 ? Math.PI / 2 - low : low;
    }
    const vh = v * Math.cos(theta);
    const vz = v * Math.sin(theta);
    p.vx[pi] = (dx / dh) * vh;
    p.vy[pi] = (dy / dh) * vh;
    p.vz[pi] = vz;
    p.ttl[pi] = (2 * vz) / GRAVITY + 0.6;
  } else {
    p.ptype[pi] = PROJ_BOLT;
    p.gravity[pi] = 0;
    p.vx[pi] = (dx / dh) * weap.projectileSpeed;
    p.vy[pi] = (dy / dh) * weap.projectileSpeed;
    p.vz[pi] = 0;
    p.ttl[pi] = weap.range / weap.projectileSpeed + 0.3;
  }
}

// --- Projectile integration + collision + damage. ---
export function projectilesStep(w: World, sim: Sim): void {
  const p = sim.proj;
  for (let i = 0; i < p.count; i++) {
    if (!p.alive[i]) continue;
    p.px[i] = p.x[i];
    p.py[i] = p.y[i];
    p.pz[i] = p.z[i];

    if (p.gravity[i] > 0) {
      p.vz[i] -= p.gravity[i] * DT;
      p.z[i] += p.vz[i] * DT;
    }
    p.x[i] += p.vx[i] * DT;
    p.y[i] += p.vy[i] * DT;
    p.ttl[i] -= DT;

    let hit = false;
    let hitX = p.x[i];
    let hitY = p.y[i];

    // Ballistic rounds detonate on reaching the ground plane.
    if (p.gravity[i] > 0 && p.z[i] <= 0) {
      hit = true;
    } else {
      // Proximity collision (sampled around current position).
      const hitR = Math.max(5, Math.hypot(p.vx[i], p.vy[i]) * DT * 0.6);
      let target = -1;
      let td = Infinity;
      sim.hash.query(p.x[i], p.y[i], hitR + 14, (c) => {
        const oc = w.owner[c];
        if (oc === OWNER_NEUTRAL || oc === p.owner[i]) return;
        if (!(w.kind[c] === KIND_UNIT || w.kind[c] === KIND_BUILDING)) return;
        const d = dist(p.x[i], p.y[i], w.posX[c], w.posY[c]);
        if (d <= w.def(c).radius + hitR && d < td) {
          td = d;
          target = c;
        }
      });
      if (target >= 0) {
        hit = true;
        hitX = w.posX[target];
        hitY = w.posY[target];
        if (p.aoe[i] <= 0) {
          applyDamage(w, target, p.damage[i]);
          sim.fxEvents.push({ type: "spark", x: hitX, y: hitY, radius: 6, faction: p.faction[i] });
        }
      }
    }

    if (!hit && p.ttl[i] <= 0) {
      hit = true;
    }

    if (hit) {
      if (p.aoe[i] > 0) {
        detonate(w, sim, -1, p.damage[i], p.aoe[i], hitX, hitY, p.owner[i]);
        sim.fxEvents.push({ type: "explosion", x: hitX, y: hitY, radius: p.aoe[i], faction: p.faction[i] });
      }
      p.kill(i);
    }
  }
}

/** Splash damage with reduced friendly-fire. `source` (-1 if none) is skipped. */
function detonate(
  w: World,
  sim: Sim,
  source: number,
  damage: number,
  aoe: number,
  x: number,
  y: number,
  owner?: number,
): void {
  const dmgOwner = owner !== undefined ? owner : source >= 0 ? w.owner[source] : OWNER_NEUTRAL;
  sim.hash.query(x, y, aoe, (c) => {
    if (c === source || !w.alive[c]) return;
    if (!(w.kind[c] === KIND_UNIT || w.kind[c] === KIND_BUILDING)) return;
    const d = dist(x, y, w.posX[c], w.posY[c]);
    if (d > aoe) return;
    const falloff = Math.max(0.2, 1 - d / aoe);
    let dmg = damage * falloff;
    if (w.owner[c] === dmgOwner) dmg *= FRIENDLY_FIRE_MULT;
    applyDamage(w, c, dmg);
  });
}

function applyDamage(w: World, c: number, dmg: number): void {
  w.health[c] -= dmg;
}

// --- Deaths -> wreckage + big explosions. ---
export function deathsStep(w: World, sim: Sim): boolean {
  let anyCommanderDied = false;
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e]) continue;
    if (w.health[e] > 0) continue;
    const def = w.def(e);
    if (w.kind[e] === KIND_UNIT || w.kind[e] === KIND_BUILDING) {
      if (def.role === "commander" && def.suicide) {
        detonate(w, sim, e, def.suicide.damage, def.suicide.aoeRadius, w.posX[e], w.posY[e]);
        sim.fxEvents.push({ type: "explosion", x: w.posX[e], y: w.posY[e], radius: def.suicide.aoeRadius, faction: factionIdx(w, e), big: true });
        anyCommanderDied = true;
      } else {
        sim.fxEvents.push({ type: "explosion", x: w.posX[e], y: w.posY[e], radius: Math.max(14, def.radius * 1.8), faction: factionIdx(w, e) });
      }
      // Bombs (buildProgress zeroed on detonation) and features leave no wreckage.
      const leavesWreck = def.role !== "bomb" && def.metalCost > 0 && w.buildProgress[e] > 0.05;
      if (leavesWreck) spawnWreckage(w, e);
      w.kill(e);
    } else {
      // Wreckage or feature destroyed outright.
      w.kill(e);
    }
  }
  return anyCommanderDied;
}

function factionIdx(w: World, e: number): number {
  return w.def(e).faction === "feral" ? 1 : 0;
}
